import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Button,
  Checkbox,
  FormControlLabel,
  Tooltip,
  IconButton,
  Typography,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { DebugPanel } from '@components';
import { PledgeStore } from '@app/api/PledgeStore';
import { CohortPledgeView, useCohortPledgeViewCreator } from '@app/view-creator/CohortPledgeViewCreator';
import apiResponse from '@stores/ApiResponse';
import { useQueryClient } from '@tanstack/react-query';

interface PledgeDialogProps {
  open: boolean;
  groupId: number;
  onClose: () => void;
  onSaved: () => void;
}

export const PledgeDialog: React.FC<PledgeDialogProps> = ({ open, groupId, onClose, onSaved }) => {
  const pledgeStore = new PledgeStore();
  const { data, isLoading } = useCohortPledgeViewCreator(groupId);
  const [pledge, setPledge] = useState<CohortPledgeView | null>(null);
  const [readonly, setReadonly] = useState(false);
  const queryClient = useQueryClient();

  // Ensure we have data and set readonly accordingly
  useEffect(() => {
    if (!open || !data) return;
    setPledge(data);
    setReadonly(data.finalized);
  }, [data, open]);

  // Ensure pledge is not null and all fields have proper types (amount, min, max are numbers, finalized is boolean)
  const handleChange = (field: keyof CohortPledgeView) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!pledge) return;
    const value = field === 'finalized' ? e.target.checked : Number(e.target.value.replace(/\D+/g, ''));
    setPledge({ ...pledge, [field]: value });
  };

  // Update the pledge in the DB
  const handleSave = async () => {
    if (!pledge) return;
    if (
      !window.confirm(
        'Are you sure you want to save these pledge settings? Once finalized, you can no longer edit the pledge.'
      )
    )
      return;

    try {
      await apiResponse.handleApiResponse(() => pledgeStore.updateCohortPledge(pledge));
      // Invalidate the cached view so the next dialog gets fresh data
      queryClient.invalidateQueries({
        queryKey: ['CohortPledgeViewCreator', groupId],
      });
      onSaved();
      onClose();
    } catch (err) {
      console.error('Failed to save pledge:', err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DebugPanel displayItem={pledge}></DebugPanel>
      <DialogTitle>Set Pledge</DialogTitle>
      <DialogContent style={{ paddingTop: 4 }}>
        <FormControl fullWidth margin="dense">
          <InputLabel htmlFor="pledge-amount">Total Pledge Amount</InputLabel>
          <OutlinedInput
            id="pledge-amount"
            startAdornment={<InputAdornment position="start">$</InputAdornment>}
            label="Total Pledge Amount"
            value={(Number(pledge?.individualAmount) || 0) + (Number(pledge?.externalAmount) || 0)}
            onChange={handleChange('amount')}
            disabled={true}
          />
          <Typography sx={{ fontSize: '14px', color: '#666' }}>
            This is the sum of the individual and external pledge amounts.
          </Typography>
        </FormControl>
        <FormControl fullWidth margin="dense">
          <InputLabel htmlFor="individual-pledge-amount">Individual Pledge Amount (Per Person)</InputLabel>
          <OutlinedInput
            id="individual-pledge-amount"
            startAdornment={<InputAdornment position="start">$</InputAdornment>}
            label="Individual Pledge Amount (Per Person)"
            value={pledge?.individualAmount ?? ''}
            onChange={handleChange('individualAmount')}
            disabled={isLoading || readonly}
          />
          <Typography sx={{ fontSize: '14px', color: '#666' }}>
            If finalized, this amount will be used for each individual pledge. If not finalized, it will be overwritten
            by our automatic calculations on day 5.
          </Typography>
        </FormControl>
        <FormControl fullWidth margin="dense">
          <InputLabel htmlFor="external-pledge-amount">External Pledge Amount (Per Person)</InputLabel>
          <OutlinedInput
            id="external-pledge-amount"
            startAdornment={<InputAdornment position="start">$</InputAdornment>}
            label="External Pledge Amount (Per Person)"
            value={pledge?.externalAmount ?? ''}
            onChange={handleChange('externalAmount')}
            disabled={isLoading || readonly}
          />
          <Typography sx={{ fontSize: '14px', color: '#666' }}>
            This amount will be added to the set or calculated individual pledge amount to determine the total by our
            automatic calculations on day 5.
          </Typography>
        </FormControl>
        <FormControl fullWidth margin="dense">
          <InputLabel htmlFor="pledge-min">Minimum</InputLabel>
          <OutlinedInput
            id="pledge-min"
            startAdornment={<InputAdornment position="start">$</InputAdornment>}
            label="Minimum"
            value={pledge?.min ?? ''}
            onChange={handleChange('min')}
            disabled={isLoading || readonly}
          />
        </FormControl>
        <FormControl fullWidth margin="dense">
          <InputLabel htmlFor="pledge-max">Maximum</InputLabel>
          <OutlinedInput
            id="pledge-max"
            startAdornment={<InputAdornment position="start">$</InputAdornment>}
            label="Maximum"
            value={pledge?.max ?? ''}
            onChange={handleChange('max')}
            disabled={isLoading || readonly}
          />
        </FormControl>
        <FormControlLabel
          control={
            <Checkbox
              checked={pledge?.finalized ?? false}
              onChange={handleChange('finalized')}
              disabled={isLoading || readonly}
            />
          }
          label={
            <span>
              Finalized{' '}
              <Tooltip title="If this is not checked, the individual pledge will be overwritten by our automatic calculations on day 5. If this is checked, the system will no longer calculate the pledge automatically and the individual pledge you enter here will be used. Once finalized, you will no longer be able to edit the pledge.">
                <IconButton size="small">
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </span>
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={handleSave} disabled={!pledge || isLoading || readonly}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
